/* eslint-disable react/jsx-props-no-spreading */
import { useForm } from '@folklore/forms';
import { useUrlGenerator } from '@folklore/routes';
import { useTracking } from '@folklore/tracking';
import classNames from 'classnames';
import { isString } from 'lodash';
import React, { useCallback, useState, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import { useAuthSendToken } from '../../hooks/useAuth';

import { useContest } from '../../contexts/ContestContext';
import { useLabels } from '../../contexts/LabelsContext';
import Button from '../buttons/Button';
import SubmitButton from '../buttons/SubmitButton';
import { Pincode } from '../fields';
import CheckboxField from '../fields/Checkbox';
import FieldLabel from '../fields/FieldLabel';
import PhoneField from '../fields/Phone';
import TokenModal from '../modals/TokenModal';
import Body from '../partials/Body';

import styles from '../../../styles/forms/login-form.module.css';

interface Optin {
    id: string;
    description: string;
    required?: boolean;
}

interface LoginFormProps {
    title?: React.ReactNode | false;
    formLabel?: React.ReactNode | false;
    phoneLabel?: React.ReactNode | false;
    pincodeLabel?: React.ReactNode | false;
    submitLabel?: React.ReactNode | false;
    tokenModalTitle?: React.ReactNode | false;
    tokenModalDescription?: React.ReactNode | false;
    tokenModalNote?: React.ReactNode | false;
    tokenModalResend?: React.ReactNode | false;
    tokenModalSubmitLabel?: React.ReactNode | false;
    optinsNote?: React.ReactNode | false;
    footerNote?: React.ReactNode | false;
    optins?: Optin[];
    optinsAfterFields?: boolean;
    extraFields?: Record<string, string>;
    withPincode?: boolean;
    withPincodeHelp?: boolean;
    onSuccess?: (user: Participant, isRegistering: boolean) => void;
    onClickPincodeHelp?: () => void;
    children?: React.ReactNode;
    className?: string;
    labelClassName?: string;
    inputClassName?: string;
    submitClassName?: string;
    errorsClassName?: string;
    phoneLabelClassName?: string;
    pincodeLabelClassName?: string;
}

function LoginForm({
    title = null,
    formLabel = null,
    phoneLabel = null,
    pincodeLabel = null,
    submitLabel = null,
    tokenModalTitle = null,
    tokenModalDescription = null,
    tokenModalNote = null,
    tokenModalResend = null,
    tokenModalSubmitLabel = null,
    optinsNote = null,
    footerNote = null,
    optins = null,
    optinsAfterFields = false,
    extraFields = null,
    withPincode = false,
    withPincodeHelp = false,
    onSuccess = null,
    onClickPincodeHelp = null,
    children = null,
    className = null,
    labelClassName = null,
    inputClassName = null,
    submitClassName = null,
    errorsClassName = null,
    phoneLabelClassName = null,
    pincodeLabelClassName = null,
}: LoginFormProps) {
    const route = useUrlGenerator();
    const { access = null } = useContest();
    const { pincodeMask = '0000000', profile = null } = access || {};
    const action = route('auth.token');
    const [modalOpened, setModalOpened] = useState(false);
    const [isRegistering, setRegistering] = useState(false);
    const tracking = useTracking();
    const {
        loginFormOptinsNote,
        loginFormFooterNote,
        loginFormPhone,
        loginFormSubmit,
        loginFormPincode,
        loginFormPincodeHelp,
    } = useLabels();

    const onTokenComplete = useCallback(({ token_sent: tokenSent = false, register = false }) => {
        if (tokenSent) {
            setModalOpened(true);
            setRegistering(register);
            tracking.trackInteraction('login_token_sent');
        } else {
            // @TODO showErrors
        }
    }, []);

    const onEnterComplete = useCallback(
        (user) => {
            if (onSuccess !== null) {
                onSuccess(user, isRegistering);
                if (isRegistering) {
                    tracking.trackInteraction('signup');
                }
                tracking.trackInteraction('login');
            }
        },
        [onSuccess, isRegistering, tracking],
    );

    const onTokenModalClose = useCallback(() => {
        setModalOpened(false);
    }, [onSuccess]);

    const formFields = useMemo(() => {
        const fields = ['phone', 'optins'];

        if (withPincode) {
            fields.push('pincode');
        }

        if (extraFields !== null) {
            return [...fields, ...Object.keys(extraFields)];
        }

        return fields;
    }, [withPincode, extraFields]);

    const { sendTokenAsync } = useAuthSendToken();

    const postForm = useCallback(
        (postAction, postData) => sendTokenAsync(postData),
        [sendTokenAsync],
    );

    const {
        fields,
        value,
        onSubmit,
        errors,
        loading,
        generalError = null,
    } = useForm({
        initialValue: extraFields,
        fields: formFields,
        postForm,
        action,
        onComplete: onTokenComplete,
    });

    const { optins: fieldOptins, phone: fieldPhone, pincode: fieldPincode } = fields || {};
    const { value: optinValue, onChange: onOptinChange } = fieldOptins || {};

    const { phone: phoneErrors, pincode: pincodeErrors } = errors || {};

    const allErrors = useMemo(
        () => [
            ...(optins || []).reduce(
                (optinErrors, { id }) => [
                    ...optinErrors,
                    ...((errors || {})[`optins.${id}`] || []),
                ],
                [],
            ),
            ...(pincodeErrors || []),
            ...(phoneErrors || []),
        ],
        [phoneErrors, pincodeErrors, optins, errors],
    );

    const fieldsElement = (
        <div className={styles.fields} key="fields">
            {formLabel !== null ? (
                <FieldLabel
                    className={classNames([
                        styles.label,
                        { [labelClassName]: labelClassName !== null },
                    ])}
                >
                    {formLabel}
                </FieldLabel>
            ) : null}
            <div className={styles.fields}>
                {(phoneLabel ?? loginFormPhone) !== false ? (
                    <FieldLabel
                        className={classNames([styles.label, labelClassName, phoneLabelClassName])}
                    >
                        {phoneLabel || loginFormPhone || (
                            <FormattedMessage
                                defaultMessage="Enter your phone number"
                                description="Login form field label"
                            />
                        )}
                    </FieldLabel>
                ) : null}
                <div className={styles.field}>
                    <PhoneField
                        className={classNames([
                            styles.input,
                            { [inputClassName]: inputClassName !== null },
                        ])}
                        {...fieldPhone}
                        placeholder="(514) 123-4567"
                        // required
                    />
                </div>

                {withPincode ? (
                    <>
                        {(pincodeLabel ?? loginFormPincode) !== false ? (
                            <FieldLabel
                                className={classNames([
                                    styles.label,
                                    labelClassName,
                                    pincodeLabelClassName,
                                ])}
                            >
                                {pincodeLabel || loginFormPincode || (
                                    <FormattedMessage
                                        defaultMessage="Your PIN code can be entered now or later, but is required to play."
                                        description="Login form field label"
                                    />
                                )}
                            </FieldLabel>
                        ) : null}
                        <div className={styles.field}>
                            <Pincode
                                className={classNames([
                                    styles.input,
                                    { [inputClassName]: inputClassName !== null },
                                ])}
                                {...fieldPincode}
                                placeholder={pincodeMask}
                                maxLength={10}
                            />
                        </div>
                        {withPincodeHelp ? (
                            <div className={styles.help}>
                                <Button className={styles.link} onClick={onClickPincodeHelp}>
                                    {loginFormPincodeHelp ?? (
                                        <FormattedMessage
                                            defaultMessage="Where to find my PIN?"
                                            description="Button label"
                                        />
                                    )}
                                </Button>
                            </div>
                        ) : null}
                    </>
                ) : null}
            </div>
        </div>
    );

    const optinsElement = (
        <div className={styles.optins} key="optins">
            {(optins || []).map(({ id, description }, optinI) => (
                <CheckboxField
                    key={`optin-${optinI + 1}-${id}`}
                    initialValue="true"
                    value={
                        optinValue !== null && `${optinValue[id] || 'false'}` === 'true'
                            ? 'true'
                            : null
                    }
                    onChange={(newValue) => {
                        const newObject = {
                            ...optinValue,
                            [id]: newValue,
                        };

                        onOptinChange(
                            Object.keys(newObject)
                                .filter((key) => newObject[key] === 'true')
                                .reduce((all, key) => ({ ...all, [key]: newObject[key] }), {}),
                        );
                    }}
                    className={styles.optin}
                    // required={required}
                >
                    <Body body={description} tag="span" />
                </CheckboxField>
            ))}
            {(optinsNote ?? loginFormOptinsNote) !== null ? (
                <div className={styles.optionsNote}>{optinsNote ?? loginFormOptinsNote}</div>
            ) : null}
        </div>
    );

    return (
        <>
            <form
                className={classNames([
                    styles.container,
                    {
                        [className]: className !== null,
                        [styles.loading]: loading,
                    },
                ])}
                onSubmit={onSubmit}
                action={action}
            >
                {title !== null ? <h4 className={styles.title}>{title}</h4> : null}
                {optinsAfterFields
                    ? [fieldsElement, optinsElement]
                    : [optinsElement, fieldsElement]}
                <SubmitButton
                    className={classNames([
                        styles.submit,
                        { [submitClassName]: submitClassName !== null },
                    ])}
                >
                    {submitLabel ||
                        loginFormSubmit ||
                        (profile !== null ? (
                            <FormattedMessage defaultMessage="Next" description="Button label" />
                        ) : (
                            <FormattedMessage
                                defaultMessage="Play now"
                                description="Button label"
                            />
                        ))}
                </SubmitButton>
                {(allErrors !== null && allErrors.length > 0) || generalError !== null ? (
                    <div
                        className={classNames([
                            styles.errors,
                            { [errorsClassName]: errorsClassName !== null },
                        ])}
                    >
                        {(allErrors || []).map((error, errorIndex) => (
                            <div key={`error-${errorIndex + 1}`}>{error}</div>
                        ))}
                        {generalError !== null ? <div>{generalError}</div> : null}
                    </div>
                ) : null}
                {(footerNote ?? loginFormFooterNote) !== false ? (
                    <div className={styles.footerNote}>
                        {isString(footerNote || loginFormFooterNote) ? (
                            <Body
                                body={
                                    footerNote ||
                                    loginFormFooterNote || (
                                        <FormattedMessage
                                            defaultMessage="Your phone number will be used to complete the creation of your account and to contact you via SMS if you win a prize. "
                                            description="Login note"
                                        />
                                    )
                                }
                                tag="span"
                            />
                        ) : (
                            footerNote ||
                            loginFormFooterNote || (
                                <FormattedMessage
                                    defaultMessage="Your phone number will be used to complete the creation of your account and to contact you via SMS if you win a prize. "
                                    description="Login note"
                                />
                            )
                        )}
                    </div>
                ) : null}
                {children}
            </form>
            {modalOpened ? (
                <TokenModal
                    value={value}
                    title={tokenModalTitle}
                    description={tokenModalDescription}
                    note={tokenModalNote}
                    resendLabel={tokenModalResend}
                    submitLabel={tokenModalSubmitLabel}
                    closesOnSuccess
                    onComplete={onEnterComplete}
                    onClose={onTokenModalClose}
                />
            ) : null}
        </>
    );
}

export default LoginForm;
